import React from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar/Navbar";
import Footer from "../components/Footer/Footer";

const About = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About Mukesh Rathore | Independent Music Composer & Educator</title>

        <meta
          name="description"
          content="Learn about Mukesh Rathore, an independent Indian music composer and educator with 28+ years of experience, classical training, original compositions, and a mission to help first-time indie music creators."
        />

        <meta
          property="og:title"
          content="About Mukesh Rathore | Independent Music Composer & Educator"
        />

        <meta
          property="og:description"
          content="The journey of Mukesh Rathore — independent music composer and educator with 28+ years of experience, blending classical training with modern indie music creation."
        />
        <link rel="canonical" href="https://mukeshrathore.com/about" />
      </Helmet>

      <Navbar />
      
      {/* Hero Section */}
      <section className="py-16 px-6 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
        <div className="max-w-6xl mx-auto text-center">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold mb-6"
          >
            About Me
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl text-purple-100 italic"
          >
            28 years with music. Still creating. Still learning. Still guiding.
          </motion.p>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 md:order-1"
            >
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Hi, I'm Mukesh Rathore
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                An artist, composer, producer, and music mentor.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                My journey with music spans <strong>28 years</strong>, combining formal training, real-world performance, studio work, and consistent original music creation.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                For over <strong>9 years</strong>, I've been teaching singing and music fundamentals through my YouTube channel <strong>RagaSpectrum Online Music Academy</strong>, where 15,000+ learners have followed my tutorials to understand music in a simple, practical way.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                But teaching is only one part of my story.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 md:order-2 flex justify-center"
            >
              <img 
                src="/images/profile3.png" 
                alt="Mukesh Rathore" 
                loading="lazy"
                className="w-full max-w-md rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 md:p-12 mb-16"
          >
            <h3 className="text-3xl font-bold text-gray-900 mb-6">
              I am also a working independent artist who consistently creates and releases original music.
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              To date, I've released <a href="/music" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800 font-bold underline"><strong>45+ Original songs</strong></a> across multiple genres, including romantic, family-oriented, devotional, patriotic, children's music, and instrumental compositions.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed italic">
              This ongoing creative work deeply influences how I mentor — because I teach from experience, not just explanation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Musical Foundation */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              My Musical Foundation
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              My approach to music is rooted in both classical discipline and modern application.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <div className="text-4xl mb-4">🎓</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                6-year Diploma in Indian Classical Vocal Music
              </h3>
              <p className="text-gray-700">
                Prayaga Sangeet Samiti, Allahabad (Prayagraj)
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <div className="text-4xl mb-4">🎸</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                1 year of Guitar Training
              </h3>
              <p className="text-gray-700">
                Understanding harmony, chords, and accompaniment
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <div className="text-4xl mb-4">🎧</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                1 year of Sound Engineering Training
              </h3>
              <p className="text-gray-700">
                From a professional music studio in Mumbai
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-8 shadow-lg"
            >
              <div className="text-4xl mb-4">🎤</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                Many years of live performance experience
              </h3>
              <p className="text-gray-700">
                Performing across diverse formats and audiences internationally, including the USA, Dubai, and India
              </p>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-lg text-gray-700 text-center mt-12 max-w-4xl mx-auto leading-relaxed"
          >
            This blend of classical training, technical knowledge, and live-stage experience allows me to guide students with balance — respecting tradition while embracing today's tools.
          </motion.p>
        </div>
      </section>

      {/* Teaching & Mentorship */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Teaching & Mentorship Journey
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Empowering musicians through structured learning and personalized guidance
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-8 shadow-lg"
            >
              <div className="text-4xl mb-4">📺</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                RagaSpectrum Music Academy
              </h3>
              <p className="text-gray-700 mb-4">
                For <strong>9+ years</strong>, I've been teaching singing and music fundamentals through my YouTube channel, making music education accessible to everyone.
              </p>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-3xl">👥</span>
                <span className="text-lg font-semibold text-purple-600">15,000+ subscribers</span>
              </div>
              <a
                href="/tutorial"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-6 rounded-lg transition-all"
              >
                Explore Free Tutorials →
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-8 shadow-lg"
            >
              <div className="text-4xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">
                1-on-1 Music Coaching
              </h3>
              <p className="text-gray-700 mb-4">
                Beyond group learning, I've provided personalized coaching to many aspiring musicians, helping them overcome unique challenges and achieve their musical goals.
              </p>
              <p className="text-gray-600 italic">
                Tailored guidance for individual growth and breakthrough
              </p>
            </motion.div>
          </div>

          
        </div>
      </section>

      {/* Not Just Teaching */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              Not Just Teaching — Actively Creating
            </h2>
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl p-8 md:p-12">
              <p className="text-2xl md:text-3xl font-bold mb-6">
                One belief guides everything I do:
              </p>
              <p className="text-xl md:text-2xl italic">
                "You cannot teach creation without creating yourself."
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-6 text-lg text-gray-700 leading-relaxed"
          >
            <p className="text-xl font-semibold text-gray-900">
              Every original song I release refines my understanding of:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="flex items-start">
                <span className="text-purple-600 mr-3">•</span>
                <span>Melody and emotion</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3">•</span>
                <span>Arrangement and production</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3">•</span>
                <span>Audience connection</span>
              </li>
              <li className="flex items-start">
                <span className="text-purple-600 mr-3">•</span>
                <span>Real challenges indie artists face</span>
              </li>
            </ul>
            <p className="text-lg italic pt-6">
              That lived experience flows directly into my teaching.
            </p>
            <p className="text-xl font-semibold text-gray-900 pt-4">
              When you learn from me, you're not learning from theory alone — you're learning from someone who is actively doing the work, consistently.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why I Do What I Do */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8 text-center">
              Why I Do What I Do
            </h2>
            <p className="text-2xl text-gray-800 mb-8 text-center font-semibold">
              I believe music is every human's birthright, not a privilege reserved for trained musicians or industry insiders.
            </p>

            <div className="bg-white rounded-2xl p-8 md:p-12 shadow-xl mb-8">
              <p className="text-xl text-gray-700 mb-6">
                Too many passionate creators give up because:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-2xl">✗</span>
                  <span className="text-lg text-gray-700">They think they're "not trained enough"</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-2xl">✗</span>
                  <span className="text-lg text-gray-700">They don't play instruments</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-2xl">✗</span>
                  <span className="text-lg text-gray-700">They feel music theory is overwhelming</span>
                </li>
                <li className="flex items-start">
                  <span className="text-red-500 mr-3 text-2xl">✗</span>
                  <span className="text-lg text-gray-700">They lack a clear, step-by-step path</span>
                </li>
              </ul>
            </div>

            <p className="text-2xl text-gray-800 font-semibold mb-6 text-center">
              My work is about removing these barriers.
            </p>

            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-2xl p-8 text-center">
              <p className="text-xl mb-4">I help everyday people move from:</p>
              <p className="text-3xl font-bold mb-2">Humming melodies in private</p>
              <p className="text-2xl mb-4">to</p>
              <p className="text-3xl font-bold">Composing, producing, and releasing original music</p>
              <p className="text-lg mt-6 italic">—with systems that are simple, practical, and achievable.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What I Help You Achieve */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-12 text-center">
              What I Help You Achieve
            </h2>

            <p className="text-xl text-gray-700 mb-8">
              Through my framework, I help aspiring musicians:
            </p>

            <ul className="space-y-4 mb-12">
              <li className="flex items-start">
                <span className="text-green-500 mr-3 text-2xl">✓</span>
                <span className="text-lg text-gray-700">Create original melodies and songs (even without instruments)</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 text-2xl">✓</span>
                <span className="text-lg text-gray-700">Understand music practically, without heavy theory</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 text-2xl">✓</span>
                <span className="text-lg text-gray-700">Produce songs from home using modern tools</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 text-2xl">✓</span>
                <span className="text-lg text-gray-700">Release their first original song with confidence</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-3 text-2xl">✓</span>
                <span className="text-lg text-gray-700">See themselves as independent indie artists</span>
              </li>
            </ul>

            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-8 md:p-12 text-center">
              <p className="text-xl text-gray-700 mb-4">This work has evolved into my flagship platform:</p>
              <h3 className="text-4xl md:text-5xl font-bold text-purple-900 mb-6">
                Indie Music Creators
              </h3>
              <p className="text-xl text-gray-700 mb-8">
                A growing community built to help first-time creators <strong>Create • Produce • Launch</strong> their original song—step by step, within 90 days.
              </p>
              <button
                onClick={() => window.open('https://indiemusiccreators.com/', '_blank')}
                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-8 rounded-lg text-lg transition-all transform hover:scale-105 shadow-xl"
              >
                Explore Indie Music Creators →
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-12">
              My Mission
            </h2>
            
            <div className="space-y-6 text-xl md:text-2xl">
              <p className="flex items-center justify-center">
                <span className="mr-3">👉</span>
                <span>To empower hidden talents to become confident creators</span>
              </p>
              <p className="flex items-center justify-center">
                <span className="mr-3">👉</span>
                <span>To bridge classical foundation with modern independence</span>
              </p>
              <p className="flex items-center justify-center">
                <span className="mr-3">👉</span>
                <span>To help people stop waiting and start releasing music</span>
              </p>
            </div>

            <div className="mt-16 pt-12 border-t border-white/30">
              <p className="text-2xl md:text-3xl mb-6 font-light italic">
                If music has stayed with you for years,<br />
                it's not an accident.
              </p>
              <p className="text-3xl md:text-4xl font-bold">
                You don't need permission.<br />
                You need guidance from someone who's walked the path.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default About;
